/**
 * ONUTechnicalReportDialog - Technical report for ONU installation
 */
import React from 'react';
import { ONUWithDetails, Pole } from '../../types';
import './Dialog.css';
import './TechnicalReportDialog.css';

interface ONUTechnicalReportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onu: ONUWithDetails;
  pole?: Pole;
}

const FIBER_COLOR_MAP: Record<string, string> = {
  'blue': 'Modrá',
  'orange': 'Oranžová',
  'green': 'Zelená',
  'brown': 'Hnedá',
  'gray': 'Sivá',
  'white': 'Biela',
  'red': 'Červená',
  'black': 'Čierna',
  'yellow': 'Žltá',
  'violet': 'Fialová',
  'pink': 'Ružová',
  'turquoise': 'Tyrkysová',
};

const STATUS_MAP: Record<string, string> = {
  'pending': '⏳ Čaká na inštaláciu',
  'active': '✅ Aktívne',
  'inactive': '⏸️ Neaktívne',
  'offline': '❌ Offline',
};

export const ONUTechnicalReportDialog: React.FC<ONUTechnicalReportDialogProps> = ({
  isOpen,
  onClose,
  onu,
  pole,
}) => {
  if (!isOpen) return null;

  const printReport = () => {
    window.print();
  };

  // Get current date/time for report
  const reportDate = new Date().toLocaleString('sk-SK', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog technical-report-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header no-print">
          <h2>📋 Technická správa - ONU {onu.name}</h2>
          <button className="dialog-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="dialog-content technical-report-content">
          {/* Print Header */}
          <div className="print-only report-print-header">
            <h1>TECHNICKÁ SPRÁVA - INŠTALÁCIA ONU</h1>
            <div className="report-meta">
              <div>ONU: <strong>{onu.name}</strong></div>
              <div>Dátum: <strong>{reportDate}</strong></div>
            </div>
          </div>

          {/* Print/Close buttons */}
          <div className="report-actions no-print">
            <button className="btn-secondary" onClick={onClose}>
              Zatvoriť
            </button>
            <button className="btn-primary" onClick={printReport}>
              🖨️ Tlačiť
            </button>
          </div>

          {/* ONU Information */}
          <section className="report-section">
            <h3>📡 INFORMÁCIE O ONU</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Názov ONU:</span>
                <span className="info-value"><strong>{onu.name}</strong></span>
              </div>
              {onu.customer_name && (
                <div className="info-item">
                  <span className="info-label">Zákazník:</span>
                  <span className="info-value"><strong>{onu.customer_name}</strong></span>
                </div>
              )}
              <div className="info-item">
                <span className="info-label">Stav:</span>
                <span className="info-value">{STATUS_MAP[onu.status] || onu.status}</span>
              </div>
              {onu.onu_model && (
                <div className="info-item">
                  <span className="info-label">Model zariadenia:</span>
                  <span className="info-value">{onu.onu_model}</span>
                </div>
              )}
              {onu.serial_number && (
                <div className="info-item">
                  <span className="info-label">Sériové číslo:</span>
                  <span className="info-value">{onu.serial_number}</span>
                </div>
              )}
              {onu.mac_address && (
                <div className="info-item">
                  <span className="info-label">MAC adresa:</span>
                  <span className="info-value">{onu.mac_address}</span>
                </div>
              )}
              {onu.ip_address && (
                <div className="info-item">
                  <span className="info-label">IP adresa:</span>
                  <span className="info-value">{onu.ip_address}</span>
                </div>
              )}
              {onu.vlan_id && (
                <div className="info-item">
                  <span className="info-label">VLAN ID:</span>
                  <span className="info-value">{onu.vlan_id}</span>
                </div>
              )}
              {onu.latitude && onu.longitude && (
                <div className="info-item">
                  <span className="info-label">GPS súradnice:</span>
                  <span className="info-value">
                    {typeof onu.latitude === 'number' ? onu.latitude.toFixed(6) : parseFloat(onu.latitude).toFixed(6)},{' '}
                    {typeof onu.longitude === 'number' ? onu.longitude.toFixed(6) : parseFloat(onu.longitude).toFixed(6)}
                  </span>
                </div>
              )}
            </div>
          </section>

          {/* Connection Information */}
          <section className="report-section">
            <h3>🔌 INŠTALAČNÉ POKYNY - PRIPOJENIE</h3>
            
            <div className="installation-box">
              <h4>📍 Kde pripojiť ONU:</h4>
              
              {pole && (
                <div className="connection-info">
                  <div className="table-scroll-wrapper">
                    <table className="tech-table">
                      <tbody>
                        <tr>
                          <td className="label-cell"><strong>Stĺp:</strong></td>
                          <td className="value-cell">
                            <span className="highlight-value">{pole.name}</span>
                            {pole.latitude && pole.longitude && (
                              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                GPS: {typeof pole.latitude === 'number' ? pole.latitude.toFixed(6) : parseFloat(pole.latitude as string).toFixed(6)},{' '}
                                {typeof pole.longitude === 'number' ? pole.longitude.toFixed(6) : parseFloat(pole.longitude as string).toFixed(6)}
                              </div>
                          )}
                        </td>
                      </tr>
                      {onu.splitter_index !== undefined && (
                        <tr>
                          <td className="label-cell"><strong>Splitter:</strong></td>
                          <td className="value-cell">
                            <span className="highlight-value">Splitter č.{onu.splitter_index + 1}</span>
                            {pole.cassette?.splitters?.[onu.splitter_index] && (
                              <span style={{ marginLeft: '8px', color: '#666' }}>
                                ({pole.cassette.splitters[onu.splitter_index].type})
                              </span>
                            )}
                          </td>
                        </tr>
                      )}
                      {onu.splitter_port !== undefined && (
                        <tr>
                          <td className="label-cell"><strong>Port:</strong></td>
                          <td className="value-cell">
                            <span className="highlight-value">Port #{onu.splitter_port}</span>
                          </td>
                        </tr>
                      )}
                      {onu.fiber_color && (
                        <tr>
                          <td className="label-cell"><strong>Farba vlákna:</strong></td>
                          <td className="value-cell">
                            <span className="highlight-value fiber-color-badge">
                              {FIBER_COLOR_MAP[onu.fiber_color] || onu.fiber_color}
                            </span>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  </div>
                </div>
              )}

              {!pole && (
                <div className="warning-box">
                  ⚠️ Informácie o stĺpe nie sú dostupné
                </div>
              )}
            </div>
          </section>

          {/* Installation Checklist */}
          <section className="report-section">
            <h3>✓ KONTROLNÝ ZOZNAM INŠTALÁCIE</h3>
            <div className="checklist">
              <div className="table-scroll-wrapper">
                <table className="tech-table">
                <thead>
                  <tr>
                    <th style={{ width: '60px' }}>OK</th>
                    <th>Úloha</th>
                    <th style={{ width: '150px' }}>Iniciály</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>☐</td>
                    <td>Overenie trasovanie a označenie káblov</td>
                    <td></td>
                  </tr>
                  <tr>
                    <td>☐</td>
                    <td>Vyčistenie koncov vlákien pred zvarom</td>
                    <td></td>
                  </tr>
                  <tr>
                    <td>☐</td>
                    <td>Pripojenie na splitter port #{onu.splitter_port || '___'} (farba: {onu.fiber_color ? FIBER_COLOR_MAP[onu.fiber_color] : '___'})</td>
                    <td></td>
                  </tr>
                  <tr>
                    <td>☐</td>
                    <td>Inštalácia ONU zariadenia u zákazníka</td>
                    <td></td>
                  </tr>
                  <tr>
                    <td>☐</td>
                    <td>Pripojenie optického kábla k ONU</td>
                    <td></td>
                  </tr>
                  <tr>
                    <td>☐</td>
                    <td>Pripojenie napájania ONU</td>
                    <td></td>
                  </tr>
                  <tr>
                    <td>☐</td>
                    <td>Overenie LED indikátorov na ONU (PON, LOS, Power)</td>
                    <td></td>
                  </tr>
                  <tr>
                    <td>☐</td>
                    <td>Meranie optického výkonu (RX/TX)</td>
                    <td></td>
                  </tr>
                  <tr>
                    <td>☐</td>
                    <td>Konfigurácia ONU (VLAN, IP adresa)</td>
                    <td></td>
                  </tr>
                  <tr>
                    <td>☐</td>
                    <td>Test konektivity (ping, speedtest)</td>
                    <td></td>
                  </tr>
                  <tr>
                    <td>☐</td>
                    <td>Dokumentácia inštalácie (fotky, poznámky)</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
              </div>
            </div>
          </section>

          {/* Measurements */}
          <section className="report-section">
            <h3>📊 MERANIA A POZNÁMKY</h3>
            <div className="table-scroll-wrapper">
              <table className="tech-table">
              <tbody>
                <tr>
                  <td style={{ width: '200px' }}><strong>Optický výkon RX:</strong></td>
                  <td>__________ dBm</td>
                </tr>
                <tr>
                  <td><strong>Optický výkon TX:</strong></td>
                  <td>__________ dBm</td>
                </tr>
                {onu.signal_strength && (
                  <tr>
                    <td><strong>Zaznamenaný signál:</strong></td>
                    <td>{onu.signal_strength} dBm</td>
                  </tr>
                )}
                <tr>
                  <td><strong>Dátum inštalácie:</strong></td>
                  <td>__________</td>
                </tr>
                <tr>
                  <td><strong>Technik:</strong></td>
                  <td>__________</td>
                </tr>
              </tbody>
            </table>
            </div>

            {onu.notes && (
              <div style={{ marginTop: '16px' }}>
                <strong>Poznámky:</strong>
                <div style={{ 
                  marginTop: '8px', 
                  padding: '12px', 
                  backgroundColor: '#f9fafb', 
                  borderRadius: '6px',
                  whiteSpace: 'pre-wrap'
                }}>
                  {onu.notes}
                </div>
              </div>
            )}

            <div style={{ marginTop: '16px' }}>
              <strong>Dodatočné poznámky technika:</strong>
              <div style={{ 
                marginTop: '8px', 
                padding: '12px', 
                backgroundColor: '#f9fafb', 
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                minHeight: '100px'
              }}>
                {/* Empty space for handwritten notes */}
              </div>
            </div>
          </section>

          {/* Footer */}
          <div className="report-footer print-only">
            <div>Podpis technika: _______________________</div>
            <div>Podpis zákazníka: _______________________</div>
          </div>
        </div>
      </div>
    </div>
  );
};

